<template>
  <div>
    <popup2 :value="!!event" @input="event = null" width="428">
      <edit-field
        v-if="event === 'addGroup'"
        :type="'chips'"
        :title="'Add user to groups'"
        :field="'Groups'"
        :value="[]"
      />
      <component
        v-else-if="event"
        :entity="{}"
        :is="event"
        @save="saveHandler"
        @close="event = null"
      />
    </popup2>
    <table-page
      :selected-out="eValue"
      :toolbar="toolbar"
      :table="table"
      :entity="true"
      @event="eventHandler"
    />
  </div>
</template>
<script>
import pageActions from './pageActions';
import useSettings from '@/api/tableSettings';
import selectedActions from './selectedActions';
import useMedia from '@/compositions/useMedia';
import useAuth from '@/api/auth';

import {
  ref,
  computed,
  onMounted,
  watch,
  reactive,
  toRefs,
  onUnmounted
} from '@vue/composition-api';
import toSnakeCase from 'lodash.snakecase';
import useUsers from '@/api/users';
import useGroups from '@/api/groups';
import { nanoid } from 'nanoid';
import { RELOAD_INTERVAL_SEC } from '@/utils/constants';
import cloneDeep from "lodash.clonedeep";

export default {
  name: 'users',
  props: ['groupId'],
  metaInfo: {
    titleTemplate: '%s | Users'
  },
  components: {
    addNew: () => import('@/components/app-entity/modals/CreateUser.vue')
  },
  setup(props, { root }) {
    const { groups: pureGroupsApi } = new useGroups();
    const { users: usersApi, groups: groupsApi } = useUsers();
    const { load: loadSettings, save: saveSettings } = useSettings();
    const { computeIcon } = useMedia();

    const {
      total,
      fields,
      list,
      load: loadUser,
      params,
      loading,
      setParams
    } = usersApi;

    const event = ref(null);
    const eValue = ref([]);
    const visibleColumns = ref([]);
    const allColumns = ref(['login', 'enabled', 'mEmail', 'mPhone', 'type']);

    const { profile } = useAuth();

    let timerId = null;
    const toolbar = ref({
      title: 'users',
      pageActions,
      selectedActions
    });
    const settings = computed(() => {
      return visibleColumns.value;
    });
    const columns = computed(() => {
      return allColumns.value.map(item => ({
        key: item,
        text: root.$t(`user.${item}`),
        value: item
      }));
    });
    const items = computed(() =>
      list.value.map(i => ({
        ...i,
        type: i?.type ? i.type.toLowerCase() : '',
        avatar: computeIcon(i),
        iconFallback: 'mdi-account'
      }))
    );
    const count = computed(() => total.value);
    const state = reactive({
      usersGroup: [],
      table: {
        filters: [],
        total: count,
        items,
        columns,
        settings,
        loading: false,
        itemRoute: 'usersTableComponent'
      }
    });
    function eventHandler({ event: e, data }) {
      eValue.value = data;

      switch (e) {
        case 'setSettings': {
          visibleColumns.value = data;
          saveSettings('usersSettings', data);
          break;
        }
        case 'delete': {
          usersApi.remove(data.map(i => ({ id: i.id }))).then(() => {
            load();
            eValue.value = [];
          });
          break;
        }
        case 'massiveEnableUsers': {
          toggleUsersEnabled(true);
          break;
        }
        case 'massiveDisableUsers': {
          toggleUsersEnabled(false);
          break;
        }
        case 'addNew': {
          event.value = 'addNew';
          break;
        }
        case 'addGroup': {
          event.value = 'addGroup';
          break;
        }
        case 'setFilters': {
          state.table.filters = cloneDeep(data);

          let filter = {};
          data
            .filter(i => {
              if (Array.isArray(i.model) && i.model.length > 0) {
                return true;
              } else if (!Array.isArray(i.model) && i.model.value !== null) {
                return true;
              }
              return false;
            })
            .forEach(i => {
              filter = {
                ...filter,
                ...i?.getFilter()
              };
            });

          setParams({ filter, offset: 0 });
          load(filter);
          break;
        }
        case 'filteringTable': {
          const result = {};

          if (data.itemsPerPage) {
            result.first = data.itemsPerPage;
          }

          result.offset = (data.page - 1) * data.itemsPerPage;

          if (data.desc) {
            setParams({ desc: data.desc.length ? data.desc[0] : false });
          }

          if (data.sort) {
            if (data.sort.length) {
              setParams({ sort: data.sort[0] });
            } else {
              setParams({ sort: null });
              setParams({ orderBy: 'NATURAL' });
            }
          }

          if (data.desc && params.value.sort) {
            result.orderBy = toSnakeCase(params.value.sort).toUpperCase();
            result.orderBy += `_${params.value.desc ? 'DESC' : 'ASC'}`;
          }

          setParams(result);
          load();
          break;
        }
      }
    }

    function toggleUsersEnabled(status) {
      usersApi
        .updateUsers(
          eValue.value.map(i => ({
            id: i.id,
            enabled: status
          }))
        )
        .then(() => {
          load();
          event.value = null;
          eValue.value = [];
        });
    }

    function saveHandler(data) {
      switch (event.value) {
        case 'addNew': {
          usersApi
            .save({
              user: {
                login: data.login,
                password: data.password,
                showhidden: !!data.showhidden,
                enabled: !!data.enabled,
                description: data.description,
                mName: data.mName,
                type: data.type,
                mEmail: data.mEmail,
                mIcon: data.mIcon || null,
                mPicture: data.mPicture || null,
                mTags: data.tags?.join(',') || null,
                mPhone: data.mPhone,
                mExternalId: data.mExternalId || undefined,
                defaultEditorgroup: data.defaultEditorgroup || null,
                defaultReadergroup: data.defaultReadergroup || null,
                defaultUsergroup: data.defaultUsergroup || null
              }
            })
            .then(r => {
              const promises = [];
              let groups = [];
              if (data?.userGroups?.length > 0) groups = data?.userGroups;
              if (data?.usersToGroups?.length > 0) groups = data?.usersToGroups;
              groups.forEach(i => {
                promises.push(
                  groupsApi.addToGroup({
                    userId: r.data.createUser.user.id,
                    userGroupId: i.id
                  })
                );
              });
              Promise.all(promises).then(() => {
                event.value = null;
                root.$router.push({
                  name: 'usersTableComponent',
                  params: { id: r.data.createUser.user.id }
                });
                load();
              });
            });
          break;
        }
      }
    }

    watch(
      () => props.groupId,
      () => {
        clearInterval(timerId);
        init();
      }
    );

    function load(filterOuter) {
      const savedFilter = {};

      if (root.$route.query.filterTable !== undefined) {
        savedFilter.login = {
          includesInsensitive: root.$route.query.filterTable
        };
      }

      if (props.groupId !== 'all') {
        pureGroupsApi.setParams({ filter: { id: { equalTo: props.groupId } } });
        pureGroupsApi.load().then(r => {
          if (r.userGroups.length) {
            const title = r.userGroups[0].groupName;
            toolbar.value.title = title;
          }
        });

        setParams({
          filter: {
            ...params.value.filter,
            usersToGroupsConnection: {
              some: { userGroupId: { equalTo: props.groupId } }
            },
            ...filterOuter,
            ...savedFilter
          }
        });

        loadUser();
      } else {
        toolbar.value.title = 'Users';
        setParams({
          filter: {
            ...params.value.filter,
            ...filterOuter,
            ...savedFilter
          }
        });
        loadUser();
      }
    }

    watch(
      () => root.$route.query.filterTable,
      () => {
        load();
      }
    );

    onUnmounted(() => {
      clearInterval(timerId);
      setParams(null);
    });

    async function init() {
      setParams({
        filter: {},
        first:
          profile.value?.objectProperties.find(
            item => item.key === 'programNumbersOfRows'
          )?.value || 20
      });

      timerId = setInterval(load, RELOAD_INTERVAL_SEC);

      const setts = loadSettings('usersSettings');

      if (setts) {
        visibleColumns.value = setts;
      } else {
        visibleColumns.value = allColumns.value;
      }

      await load();
      await pureGroupsApi.load().then(data => {
        state.usersGroup = data.userGroups.map(group => ({
          id: group.id,
          value: group.id,
          name: group.groupName
        }));
      });
      await root.$nextTick();
      const filters = [
        {
          id: 'enabled',
          title: 'Status',
          getFilter() {
            return {
              enabled: {
                in: this.model.map(item => item.value)
              }
            };
          },
          model: [],
          type: 'tags',
          values: [
            { name: 'Enabled', id: nanoid(), value: true },
            { name: 'Disabled', id: nanoid(), value: false }
          ]
        },
        {
          id: 'type',
          title: 'Type',
          type: 'tags',
          getFilter() {
            return {
              type: {
                in: this.model.map(item => item.value)
              }
            };
          },
          model: [],
          values: [
            { name: 'Person', id: nanoid(), value: 'USER' },
            { name: 'App', id: nanoid(), value: 'APP' }
          ]
        },
        {
          id: 'showHidden',
          title: 'Show hidden',
          getFilter() {
            return {
              showhidden: {
                in: this.model.map(item => item.value)
              }
            };
          },
          type: 'tags',
          model: [],
          values: [
            { name: 'Yes', id: nanoid(), value: true },
            { name: 'No', id: nanoid(), value: false }
          ]
        }
      ];
      state.table.filters = cloneDeep(filters);
      state.table.emptyFilters = cloneDeep(filters);
    }

    onMounted(async () => {
      clearInterval(timerId);
      init();
    });
    return {
      ...toRefs(state),
      event,
      toolbar,
      eventHandler,
      saveHandler,
      eValue
    };
  }
};
</script>
